<template>
    <section
        :id="slice.primary.anchorName || undefined"
        ref="section"
        class="slice slice-hero slice-hero--strategy"
        aria-label="hero"
        data-testid="slice-hero-strategy"
    >
        <div class="slice-content">
            <HeroStrategyShapes :image-url="slice.primary.image?.url" />

            <div class="slice-hero--strategy__content">
                <PrismicImage v-if="isFilled.image(slice.primary.logo)" :field="slice.primary.logo" class="slice-hero__logo" />
                <PrismicRichText
                    :field="slice.primary.title"
                    class="slice-hero__title slice-hero--strategy__title"
                    data-testid="slice-hero-strategy-title"
                />
                <PrismicRichText
                    v-if="isFilled.richText(slice.primary.description)"
                    :field="slice.primary.description"
                    class="slice-hero__description slice-hero--strategy__description"
                    data-testid="slice-hero-strategy-description"
                />
                <div v-if="buttons.length > 0" class="slice-hero__ctas slice-hero--strategy__ctas">
                    <VJoyButton
                        v-for="(button, idx) in buttons"
                        :key="idx"
                        :href="$withLocalePrefix(button.url)"
                        :variant="button.variant"
                        data-component-source="hero-strategy"
                    >
                        {{ button.text }}
                    </VJoyButton>
                </div>
            </div>

            <div
                v-if="hasHubspotConfig"
                class="slice-hero--strategy__form"
                :class="{'slice-hero--strategy__form--visible': isFormReady}"
                data-testid="slice-hero-strategy-form"
            >
                <div class="slice-hero--strategy__form-card">
                    <MktHubspotForm
                        :region="slice.primary.hubspotRegion!"
                        :portal-id="slice.primary.hubspotPortalId!"
                        :form-id="slice.primary.hubspotFormId!"
                        :salesforce-id="slice.primary.salesforceId!"
                        @ready="isFormReady = true"
                    />
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed, ref, useTemplateRef} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import {getSliceComponentProps, PrismicImage, PrismicRichText} from '@prismicio/vue';
    import type {HeroSlice, HeroSliceHeroStrategy} from '@prismicio-types';
    import {MktHubspotForm} from '#components';
    import {useComponentTracking} from '#imports';
    import {VJoyButton, type TJoyButtonVariants} from '@maltjoy/core-vue';
    import HeroStrategyShapes from '../../../components/hero/HeroStrategyShapes.vue';

    const section = useTemplateRef('section');
    const isFormReady = ref(false);

    const props = defineProps(getSliceComponentProps<HeroSlice>());
    const slice = computed(() => props.slice as HeroSliceHeroStrategy);

    type CtaButton = {url: string; text: string; variant: TJoyButtonVariants | undefined};

    const buttons = computed<CtaButton[]>(() =>
        slice.value.primary.buttons
            .filter((button) => isFilled.link(button.cta))
            .map((button) => {
                const cta = button.cta as {url: string; text?: string; variant?: 'primary' | 'secondary' | 'main'};
                return {url: cta.url, text: cta.text ?? '', variant: cta.variant};
            }),
    );

    const hasHubspotConfig = computed(
        () =>
            isFilled.keyText(slice.value.primary.hubspotRegion) &&
            isFilled.keyText(slice.value.primary.hubspotPortalId) &&
            isFilled.keyText(slice.value.primary.hubspotFormId) &&
            isFilled.keyText(slice.value.primary.salesforceId),
    );

    useComponentTracking(section);
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .slice-hero {
        &--strategy {
            overflow: hidden;

            @include mq.screen_md(min) {
                min-height: 58vw;
            }

            @include mq.screen_xlg(min) {
                min-height: auto;
            }

            .slice-content {
                position: relative;

                @include mq.screen_md(min) {
                    display: flex;
                }
            }

            & &__title {
                h1 {
                    font-size: var(--joy-font-size-secondary-400);

                    strong {
                        background-color: var(--joy-color-neutral-30);
                    }

                    @include mq.screen_md(min) {
                        font-size: var(--joy-font-size-secondary-800);
                    }

                    @include mq.screen_lg(min) {
                        font-size: 56px;
                    }
                }
            }

            &__description {
                margin: var(--joy-core-spacing-8) 0 0;
                font-size: var(--joy-font-size-primary-400);
                line-height: var(--joy-line-height-small);
                padding: 0;
                max-width: 600px;

                @include mq.screen_md(min) {
                    width: auto;
                }
            }

            &__content {
                text-align: left;
                padding: var(--joy-core-spacing-8) 0 0;

                @include mq.screen_xs(min) {
                    padding-right: 20%;
                }

                @include mq.screen_md(min) {
                    width: 560px;
                    padding-right: 0;
                }

                @media screen and (min-width: 1400px) {
                    padding: 120px var(--joy-core-spacing-8);
                }
            }

            &__ctas {
                @include mq.screen_md(min) {
                    width: 125%;
                }
            }

            &__form {
                display: block;
                margin-top: var(--joy-core-spacing-12);

                @include mq.screen_md(min) {
                    text-align: center;
                    top: 0;
                    right: 0;
                    margin: 0 auto;
                    height: auto;
                    padding: 0 var(--joy-core-spacing-9);
                    width: calc(100% - 560px);
                    opacity: 0;
                    transition: opacity var(--joy-transition-duration-default) var(--joy-transition-timing-function);
                }

                &-card {
                    display: inline-block;
                    background: var(--joy-color-background-default);
                    width: 100%;
                    min-width: 40px;
                    max-width: 550px;
                    border-radius: var(--joy-core-radius-4);
                    padding: var(--joy-core-spacing-8) var(--joy-core-spacing-6);
                    box-shadow: 0 var(--joy-core-spacing-1) var(--joy-core-spacing-5) 0 rgba(51, 51, 48, 0.2);
                }

                &--visible {
                    @include mq.screen_md(min) {
                        opacity: 1;
                    }
                }
            }
        }
    }
</style>
